import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowLeftRight } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  convertLength,
  convertToAll,
  LENGTH_UNITS,
  type LengthUnitKey,
} from "@/lib/calculators/feetToMeter";

type B = { en: string; bn: string };
type Lang = "en" | "bn";

const T = {
  h1: { en: "Feet to Meter Converter", bn: "ফুট থেকে মিটার কনভার্টার" },
  desc: {
    en: "Convert feet to meters and back instantly. Plus inches, yards, hath (cubit), chain, miles & km — all the length units used across Bangladesh.",
    bn: "ফুট থেকে মিটার ও বিপরীতে তাৎক্ষণিক রূপান্তর। ইঞ্চি, গজ, হাত, চেইন, মাইল ও কিমি — বাংলাদেশে ব্যবহৃত সব দৈর্ঘ্য একক।",
  },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  amount: { en: "Amount", bn: "পরিমাণ" },
  from: { en: "From", bn: "থেকে" },
  to: { en: "To", bn: "তে" },
  swap: { en: "Swap", bn: "বদল" },
  result: { en: "Result", bn: "ফলাফল" },
  allUnits: { en: "All units", bn: "সব এককে" },
} as const;

const FAQ: { q: B; a: B }[] = [
  { q: { en: "How many meters in a foot?", bn: "১ ফুটে কত মিটার?" },
    a: { en: "1 foot = 0.3048 meter exactly. This has been the international definition since 1959.",
         bn: "১ ফুট = ০.৩০৪৮ মিটার (সঠিক)। ১৯৫৯ সাল থেকে এটি আন্তর্জাতিক সংজ্ঞা।" } },
  { q: { en: "What is 100 feet in meters?", bn: "১০০ ফুট কত মিটার?" },
    a: { en: "100 feet = 100 × 0.3048 = 30.48 meters.",
         bn: "১০০ ফুট = ১০০ × ০.৩০৪৮ = ৩০.৪৮ মিটার।" } },
  { q: { en: "১০০ ফুট কত মিটার?", bn: "১০০ ফুট কত মিটার?" },
    a: { en: "১০০ ফুট = ৩০.৪৮ মিটার।",
         bn: "১০০ ফুট = ৩০.৪৮ মিটার।" } },
  { q: { en: "১ হাত কত ফুট?", bn: "১ হাত কত ফুট?" },
    a: { en: "1 hath (cubit) = 1.5 feet = 0.4572 meter — the traditional Bangladeshi cubit length.",
         bn: "১ হাত = ১.৫ ফুট = ০.৪৫৭২ মিটার — ঐতিহ্যবাহী বাংলাদেশি হাত।" } },
  { q: { en: "How many feet in a meter?", bn: "১ মিটার কত ফুট?" },
    a: { en: "1 meter = 3.28084 feet, or about 3 feet 3.37 inches.",
         bn: "১ মিটার = ৩.২৮০৮৪ ফুট, প্রায় ৩ ফুট ৩.৩৭ ইঞ্চি।" } },
  { q: { en: "What is a chain?", bn: "চেইন কী?" },
    a: { en: "1 chain = 66 feet = 20.1168 meters. Used in Bangladesh land surveys (CS / RS / mauza maps).",
         bn: "১ চেইন = ৬৬ ফুট = ২০.১১৬৮ মিটার। বাংলাদেশের ভূমি জরিপে (CS / RS / মৌজা ম্যাপ) ব্যবহৃত।" } },
];

export const Route = createFileRoute("/feet-to-meter")({
  head: () => {
    const seo = buildSeo({
      title: "Feet to Meter Converter — Length Conversion",
      description:
        "Free feet to meter converter. Convert ft to m, m to ft, plus inches, yards, hath (cubit), miles. Bangladesh length conversions. ফুট থেকে মিটার কনভার্টার।",
      path: "/feet-to-meter",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Feet to Meter", path: "/feet-to-meter" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: FeetToMeterPage,
});

const QUICK_FT = [1, 5, 10, 50, 100, 500];

function stripBn(s: string): string {
  return s.replace(/[০-৯]/g, (d) => "০১২৩৪৫৬৭৮৯".indexOf(d).toString());
}

function FeetToMeterPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];

  const [bnDigits, setBnDigits] = useState(false);
  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number, d = 4) => {
    if (!Number.isFinite(n)) return "—";
    const rounded = Math.round(n * 10 ** d) / 10 ** d;
    const s = rounded.toLocaleString("en-US", { maximumFractionDigits: d });
    return useBn ? toBengaliNumerals(s) : s;
  };

  const [from, setFrom] = useState<LengthUnitKey>("foot");
  const [to, setTo] = useState<LengthUnitKey>("meter");
  const [amountStr, setAmountStr] = useState("100");

  const amount = parseFloat(amountStr) || 0;
  const result = useMemo(() => convertLength(amount, from, to), [amount, from, to]);
  const all = useMemo(() => convertToAll(amount, from), [amount, from]);

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const fromUnit = LENGTH_UNITS.find((u) => u.key === from)!;
  const toUnit = LENGTH_UNITS.find((u) => u.key === to)!;

  return (
    <CalculatorLayout
      slug="feet-to-meter"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Ruler"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-6 flex flex-wrap items-center justify-end gap-3 print:hidden">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      {/* Direction quick toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => { setFrom("foot"); setTo("meter"); }}
          className={cn(
            "rounded-full border px-4 py-1.5 text-xs font-medium transition-colors",
            from === "foot" && to === "meter" ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground",
            lang === "bn" && "font-bn",
          )}
        >
          {lang === "en" ? "Feet → Meter" : "ফুট → মিটার"}
        </button>
        <button
          type="button"
          onClick={() => { setFrom("meter"); setTo("foot"); }}
          className={cn(
            "rounded-full border px-4 py-1.5 text-xs font-medium transition-colors",
            from === "meter" && to === "foot" ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground",
            lang === "bn" && "font-bn",
          )}
        >
          {lang === "en" ? "Meter → Feet" : "মিটার → ফুট"}
        </button>
      </div>

      {/* Inputs */}
      <div className="mt-4 grid items-end gap-3 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-muted-foreground">{tr("amount")}</label>
          <input
            type="text"
            inputMode="decimal"
            value={useBn ? toBengaliNumerals(amountStr) : amountStr}
            onChange={(e) => setAmountStr(stripBn(e.target.value))}
            className="w-full rounded-xl border border-border bg-card px-4 py-3 text-2xl font-semibold tabular-nums focus:border-accent focus:outline-none"
          />
        </div>

        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-muted-foreground">{tr("from")}</label>
          <UnitSelect value={from} onChange={setFrom} lang={lang} />
        </div>

        <div className="flex items-center justify-center pb-2">
          <button
            type="button"
            onClick={swap}
            aria-label={tr("swap")}
            className="rounded-full border border-border bg-card p-2 hover:border-accent"
          >
            <ArrowLeftRight size={16} />
          </button>
        </div>

        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-muted-foreground">{tr("to")}</label>
          <UnitSelect value={to} onChange={setTo} lang={lang} />
        </div>

        <div />
      </div>

      {/* Quick chips */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          {lang === "en" ? "Quick:" : "দ্রুত:"}
        </span>
        {QUICK_FT.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => { setAmountStr(String(n)); setFrom("foot"); }}
            className="rounded-full border border-border bg-card px-3 py-1 text-xs hover:border-accent"
          >
            {num(n)} {lang === "en" ? "ft" : "ফুট"}
          </button>
        ))}
      </div>

      {/* Primary result */}
      <motion.div
        key={`${from}-${to}-${amountStr}`}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-6 rounded-2xl border border-accent/40 bg-accent/5 p-6 text-center"
      >
        <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
          {tr("result")}
        </p>
        <p className="mt-1 text-4xl font-bold tabular-nums md:text-5xl">
          {fmt(result, 4)}{" "}
          <span className="text-base font-normal text-muted-foreground">
            {lang === "en" ? toUnit.shortEn : toUnit.shortBn}
          </span>
        </p>
        <p className={cn("mt-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
          {fmt(amount, 4)} {lang === "en" ? fromUnit.en : fromUnit.bn} ={" "}
          {fmt(result, 6)} {lang === "en" ? toUnit.en : toUnit.bn}
        </p>
      </motion.div>

      {/* All units table */}
      <section className="mt-8 space-y-3">
        <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
          {tr("allUnits")}
        </h2>
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="p-2 text-left">{lang === "en" ? "Unit" : "একক"}</th>
                <th className="p-2 text-right">{lang === "en" ? "Value" : "মান"}</th>
                <th className="p-2 text-left">{lang === "en" ? "Note" : "নোট"}</th>
              </tr>
            </thead>
            <tbody>
              {all.map(({ unit, value }) => (
                <tr key={unit.key} className={cn("border-t border-border", unit.key === to && "bg-accent/5")}>
                  <td className="p-2">
                    <span className="font-medium">{lang === "en" ? unit.en : unit.bn}</span>{" "}
                    <span className="text-xs text-muted-foreground">({lang === "en" ? unit.shortEn : unit.shortBn})</span>
                  </td>
                  <td className="p-2 text-right tabular-nums">{fmt(value, value < 1 ? 6 : 4)}</td>
                  <td className={cn("p-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                    {unit.note ? (lang === "en" ? unit.note.en : unit.note.bn) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Foot to meter formula" : "ফুট থেকে মিটারে সূত্র"}
          </h2>
          <ul className={cn("ml-5 list-disc space-y-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "en" ? "1 foot = 0.3048 meter (exact, since 1959 international agreement)." : "১ ফুট = ০.৩০৪৮ মিটার (১৯৫৯ সালের আন্তর্জাতিক চুক্তি অনুযায়ী সঠিক)।"}</li>
            <li>{lang === "en" ? "1 meter = 3.28084 feet ≈ 3 ft 3.37 in." : "১ মিটার = ৩.২৮০৮৪ ফুট ≈ ৩ ফুট ৩.৩৭ ইঞ্চি।"}</li>
            <li>{lang === "en" ? "Meters = feet × 0.3048." : "মিটার = ফুট × ০.৩০৪৮।"}</li>
            <li>{lang === "en" ? "Feet = meters ÷ 0.3048." : "ফুট = মিটার ÷ ০.৩০৪৮।"}</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Hath (Cubit) — the traditional BD measurement" : "হাত — বাংলাদেশের ঐতিহ্যবাহী মাপ"}
          </h2>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "1 hath = 1.5 feet = 18 inches = 0.4572 meter. Tailors, carpenters and elders across Bangladesh still measure cloth, wood and rooms in hath. Two hath make a 'gaj' (yard) ≈ 0.9144 m."
              : "১ হাত = ১.৫ ফুট = ১৮ ইঞ্চি = ০.৪৫৭২ মিটার। দর্জি, কাঠমিস্ত্রি ও বয়স্করা এখনো কাপড়, কাঠ ও ঘর হাতে মাপেন। ২ হাত = ১ গজ ≈ ০.৯১৪৪ মিটার।"}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Chain — used in Bangladesh land surveys" : "চেইন — বাংলাদেশের ভূমি জরিপে"}
          </h2>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "1 chain = 66 feet = 20.1168 meters = 4 rods = 100 links. Bangladesh's CS, SA, RS and BS surveys (mauza maps) record plot dimensions in chains. 10 square chains = 1 acre."
              : "১ চেইন = ৬৬ ফুট = ২০.১১৬৮ মিটার = ৪ রড = ১০০ লিংক। বাংলাদেশের CS, SA, RS ও BS জরিপে (মৌজা ম্যাপ) প্লটের মাপ চেইনে লেখা থাকে। ১০ বর্গ-চেইন = ১ একর।"}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Common BD construction conversions" : "বাংলাদেশের কনস্ট্রাকশনে প্রচলিত রূপান্তর"}
          </h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "en" ? "Feet" : "ফুট"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Meters" : "মিটার"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Hath" : "হাত"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Inches" : "ইঞ্চি"}</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 5, 10, 20, 50, 66, 100, 500, 1000].map((ft) => (
                  <tr key={ft} className="border-t border-border">
                    <td className="p-2 tabular-nums">{num(ft)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(convertLength(ft, "foot", "meter"), 4)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(convertLength(ft, "foot", "hath"), 3)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(convertLength(ft, "foot", "inch"), 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Meter → feet quick reference" : "মিটার → ফুট দ্রুত রেফারেন্স"}
          </h2>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">{lang === "en" ? "Meters" : "মিটার"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Feet" : "ফুট"}</th>
                  <th className="p-2 text-right">{lang === "en" ? "Inches" : "ইঞ্চি"}</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 5, 10, 20, 50, 100, 500, 1000].map((m) => (
                  <tr key={m} className="border-t border-border">
                    <td className="p-2 tabular-nums">{num(m)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(convertLength(m, "meter", "foot"), 4)}</td>
                    <td className="p-2 text-right tabular-nums">{fmt(convertLength(m, "meter", "inch"), 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className={cn("text-lg font-semibold", lang === "bn" && "font-bn")}>
            {lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"}
          </h2>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/cm-to-feet" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              CM ↔ Feet
            </Link>
            <Link to="/land-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Land" : "জমি"}
            </Link>
            <Link to="/square-feet-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">
              {lang === "en" ? "Square feet" : "বর্গফুট"}
            </Link>
          </div>
        </section>
      </article>
    </CalculatorLayout>
  );
}

function UnitSelect({
  value, onChange, lang,
}: { value: LengthUnitKey; onChange: (v: LengthUnitKey) => void; lang: Lang }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as LengthUnitKey)}
      className={cn(
        "w-full rounded-xl border border-border bg-card px-3 py-3 text-base font-medium focus:border-accent focus:outline-none",
        lang === "bn" && "font-bn",
      )}
    >
      {LENGTH_UNITS.map((u) => (
        <option key={u.key} value={u.key}>
          {lang === "en" ? `${u.en} (${u.shortEn})` : `${u.bn} (${u.shortBn})`}
        </option>
      ))}
    </select>
  );
}
